/**
 * This component renders the standings tab and is used in ClubStandings, Gamecenter, etc
 */
import React from "react";
import {useState} from "react";
import {useQuery} from "@tanstack/react-query";
import StandingsTable from "../core/StandingsTable";
import Loading from "./Loading";
import DropdownSelect from "./DropdownSelect";

const Standings = ({type, clubID, sidebar, seasons, season_offset = 0}) => {
    const [season, setSeason] = useState(seasons[season_offset].value);
    const [seasonName, setSeasonName] = useState(seasons[season_offset].title);

    const handleSeasonChange = (e) => {
        setSeason(e.target.value);
        setSeasonName(seasons[e.target.value].title)
    };

    const seasonOptions = seasons.map((season) => ({
        value: season.value,
        label: season.title,
    }));

    const processDivisionData = (jsonData) => {
        let processedData = {
            tables: [], // Store separate tables for each division
        };

        try {
            jsonData.forEach((division) => {
                let divisionData = {
                    title: division.blip,
                    id: division.id,
                    next_game: division.next_game,
                    stats: [],
                };

                // Loop through the standings for the current division
                division.standings.forEach((record) => {
                    let ejected = false;

                    record.penalties.forEach((penalty) => {
                        if (penalty.ejected === true) {
                            ejected = true;
                        }
                    });

                    divisionData.stats.push({
                        footnotes: new Set(),
                        logo: record.logo.lg,
                        team_name: record.name,
                        games: record.games,
                        record: `${record.wins}-${record.losses}-${record.draws}`,
                        points: record.points,
                        gd: `${record.gf > record.ga ? "+" : ""}${record.gf - record.ga}`,
                        gd_sort: record.gf - record.ga,
                        w: record.wins,
                        d: record.draws,
                        l: record.losses,
                        gf: record.gf,
                        ga: record.ga,
                        team_class: `${ejected ? "strikethrough" : ""}`,
                        club: record.club,
                        penalties: record.penalties
                    });
                });

                // Sort the stats for the current division based on points, GD, games, and wins
                divisionData.stats.sort(function (a, b) {
                    return (
                        b.points - a.points ||
                        b.gd_sort - a.gd_sort ||
                        a.games - b.games ||
                        b.w - a.w
                    );
                });

                // Add the current division data to the tables array
                processedData.tables.push(divisionData);
            });
        } catch (e) {
            console.error(e);
        }

    console.log("processedData", processedData);

        return processedData;
    };

    // Fetch tables from the API
    const fetchTables = (season, clubID) =>
        fetch(
            `${
                process.env.REACT_APP_BUIHA_API_URL
            }/division/tables/?season=${season}&club=${clubID || ""}`
        )
            .then((res) => res.json())
            .then((json) => {
                return processDivisionData(json);
            })
            .catch((error) => {
                console.error("Error fetching division data:", error);
                throw new Error(error);
            });

    // React Query to fetch data
    const {isFetching, error, data} = useQuery({
        staleTime: Infinity,
        queryKey: ["division", season, clubID],
        queryFn: () => fetchTables(season, clubID),
        keepPreviousData: true,
        placeholderData: () => ({
            stats: [],
            title: "Loading...",
            next_game: null,
        }), // Set a placeholder while data is loading
    });

    if (error) {
        console.error("Error loading division data:", error);
        return <div>Error loading data</div>;
    }

    return (
        // Container for all pages
        <>
            <>
                <div className="w-full flex flex-row items-start justify-center lg:justify-between mb-2 xs:mb-3">
                    <div className="lg:block flex flex-col items-start justify-start">
                        <h2 className="leading-tight text-left text-2xl font-semibold">
                            Standings
                        </h2>
                        <p className="text-right text-darken font-light">
                            Displaying {season ? `standings for the ${seasonName.replace("-","/")} season` : "all current standings"}{" "}
                        </p>
                    </div>

                    <div className="flex flex-row items-start justify-start">
                        {/* Select season - Disabled for now TODO implement */}
                        {seasons.length > 1 ? (
                                <DropdownSelect
                                    label="Season"
                                    options={seasonOptions}
                                    value={season}
                                    onChange={handleSeasonChange}
                                    className="mr-1 xs:mr-2"
                                    disabled={isFetching}
                                />
                        ) : null}
                    </div>
                </div>

                {/* Display grid of gamelistbox */}
                {isFetching ? (
                    <Loading/>
                ) : (
                    <>

                        {data.tables && (
                            <div
                                className={`grid ${
                                    data.tables.length === 1
                                        ? "grid-cols-1"
                                        : "grid-cols-1 lg:grid-cols-2"
                                } w-full g:mx-8`}
                            >
                                {data.tables.map((table_data, index) => (

                                    <StandingsTable
                                        key={index}
                                        table_data={table_data}
                                        expanded={false}
                                        extraClasses="xs:px-1 pb-2"
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </>

            {/* if sidebar is defined, render it */}
            {sidebar ? (
                <div className="lg:block max-lg:hidden">{sidebar}</div>
            ) : (
                <></>
            )}
        </>
    );
};

export default Standings;
